import type React from 'react';
import { Editor, Range } from 'slate';
import toggleMark from '@atlas/tool/box/slate/toggleMark';
import toggleBlock from '@atlas/tool/box/slate/toggleBlock';
import areSamePoints from '@atlas/tool/box/slate/areSamePoints';
import type { TEditorFeature } from '@atlas/tool/box/slate/editorFeatures';
import handleCodeBlockIndentationKeyDownEvent from '@atlas/tool/box/slate/handleCodeBlockIndentationKeyDownEvent';
import handleSelectionDeletionKeyDownEvent from '@atlas/tool/box/slate/handleSelectionDeletionKeyDownEvent';
import handleListIndentationKeyDownEvent from '@atlas/tool/box/slate/handleListIndentationKeyDownEvent';
import handleEnterKeyDownEvent from '@atlas/tool/box/slate/handleEnterKeyDownEvent';
import handleArrowKeyDownEvent from '@atlas/tool/box/slate/handleArrowKeyDownEvent';
import handleBackspaceAtBlockStartKeyDownEvent from '@atlas/tool/box/slate/handleBackspaceAtBlockStartKeyDownEvent';
import handleTableNavigationKeyDownEvent from '@atlas/tool/box/slate/handleTableNavigationKeyDownEvent';
import handleLinkEditingKeyDownEvent from '@atlas/tool/box/slate/handleLinkEditingKeyDownEvent';
import hotkey from '@atlas/tool/box/event/hotkey';
import getNode from '@atlas/tool/box/slate/getNode';
import type { SlateLeaf } from '@atlas/renderer';

export default function handleKeyDownEvent(
    editor: Editor,
    event: React.KeyboardEvent,
    features?: TEditorFeature[],
): boolean {
    if (!editor.selection) return false;

    // Toggle marks
    for (const { hotkey, mark } of toggleMarkHotkeys) {
        if (features && !features.includes(mark)) continue;
        if (hotkey.matches(event)) {
            toggleMark(editor, mark);
            event.preventDefault();
            return true;
        }
    }

    // Toggle blocks
    for (const { hotkey, type } of toggleBlockHotkeys) {
        if (features && !features.includes(type)) continue;
        if (hotkey.matches(event)) {
            toggleBlock(editor, type);
            event.preventDefault();
            return true;
        }
    }

    // Escape from link and code marks
    if (hotkey('right', 'hidden').matches(event)) {
        if (!Range.isCollapsed(editor.selection)) return false;

        const { focus } = editor.selection;
        if (!areSamePoints(editor.selection.focus, Editor.end(editor, [focus.path[0]]))) return false;

        const { link, code, ...marks } = Editor.marks(editor) || {};
        if (!link && !code) return false;
        editor.insertNode({ ...marks, text: ' ' });

        return true;
    }

    // Escape from link and code marks
    if (hotkey(['space', 'space'], 'hidden').matches(event)) {
        if (!Range.isCollapsed(editor.selection)) return false;
        const { focus } = editor.selection;

        const node = getNode<SlateLeaf>(editor, focus.path);
        const { link, code, ...marks } = Editor.marks(editor) || {};
        if (node && (link || code) && node.text.length === focus.offset) {
            event.preventDefault();
            editor.deleteBackward('character');
            editor.insertNode({ ...marks, text: ' ' });
            return true;
        }
    }

    if (handleCodeBlockIndentationKeyDownEvent(editor, event)) return true;

    if (handleSelectionDeletionKeyDownEvent(editor, event)) return true;

    if (handleListIndentationKeyDownEvent(editor, event)) return true;

    if (handleEnterKeyDownEvent(editor, event)) return true;

    if (handleArrowKeyDownEvent(editor, event)) return true;

    if (handleBackspaceAtBlockStartKeyDownEvent(editor, event)) return true;

    if (handleTableNavigationKeyDownEvent(editor, event)) return true;

    if (handleLinkEditingKeyDownEvent(editor, event)) return true;

    return false;
}

// #region Helpers

// The label descriptor is spelled out inline: this package must not depend on
// `@atlas/text`, so `composer-hotkey-labels.ts` in @atlas/uicorn carries the same
// ids and texts into the catalog. `composer-hotkey-labels.test.ts` keeps the two
// files in sync.

// Mark hotkeys
export const boldHotkey = hotkey(
    { mac: 'cmd+b', win: 'ctrl+b' },
    'Composer',
    { id: 'lblComposerBold', defaultMessage: 'Bold' },
    20,
);
export const italicHotkey = hotkey(
    { mac: 'cmd+i', win: 'ctrl+i' },
    'Composer',
    { id: 'lblComposerItalic', defaultMessage: 'Italic' },
    21,
);
export const underlineHotkey = hotkey(
    { mac: 'cmd+u', win: 'ctrl+u' },
    'Composer',
    { id: 'lblComposerUnderline', defaultMessage: 'Underline' },
    22,
);
export const strikethroughHotkey = hotkey(
    { mac: 'shift+cmd+x', win: 'ctrl+shift+x' },
    'Composer',
    { id: 'lblComposerStrikethrough', defaultMessage: 'Strikethrough' },
    23,
);
export const codeHotkey = hotkey(
    { mac: 'cmd+e', win: 'ctrl+e' },
    'Composer',
    { id: 'lblComposerInlineCode', defaultMessage: 'Inline code' },
    24,
);
export const linkHotkey = hotkey(
    { mac: 'cmd+k', win: 'ctrl+k' },
    'Composer',
    { id: 'lblComposerLink', defaultMessage: 'Link' },
    30,
);

// Block hotkeys
export const headingOneHotkey = hotkey(
    { mac: 'shift+cmd+1', win: 'ctrl+shift+1' },
    'Composer',
    { id: 'lblComposerHeading1', defaultMessage: 'Heading 1' },
    40,
);
export const headingTwoHotkey = hotkey(
    { mac: 'shift+cmd+2', win: 'ctrl+shift+2' },
    'Composer',
    { id: 'lblComposerHeading2', defaultMessage: 'Heading 2' },
    41,
);
export const headingThreeHotkey = hotkey(
    { mac: 'shift+cmd+3', win: 'ctrl+shift+3' },
    'Composer',
    { id: 'lblComposerHeading3', defaultMessage: 'Heading 3' },
    42,
);
export const numberedListHotkey = hotkey(
    { mac: 'shift+cmd+7', win: 'ctrl+shift+7' },
    'Composer',
    { id: 'lblComposerNumberedList', defaultMessage: 'Numbered list' },
    50,
);
export const bulletedListHotkey = hotkey(
    { mac: 'shift+cmd+8', win: 'ctrl+shift+8' },
    'Composer',
    { id: 'lblComposerBulletedList', defaultMessage: 'Bulleted list' },
    51,
);
export const blockQuoteHotkey = hotkey(
    { mac: 'shift+cmd+9', win: 'ctrl+shift+9' },
    'Composer',
    { id: 'lblComposerBlockquote', defaultMessage: 'Blockquote' },
    60,
);
export const codeBlockHotkey = hotkey(
    { mac: 'shift+cmd+e', win: 'ctrl+shift+e' },
    'Composer',
    { id: 'lblComposerCodeBlock', defaultMessage: 'Code block' },
    61,
);

const toggleMarkHotkeys = [
    { mark: 'link', hotkey: linkHotkey },
    { mark: 'bold', hotkey: boldHotkey },
    { mark: 'code', hotkey: codeHotkey },
    { mark: 'italic', hotkey: italicHotkey },
    { mark: 'underline', hotkey: underlineHotkey },
    { mark: 'strikethrough', hotkey: strikethroughHotkey },
] as const;

const toggleBlockHotkeys = [
    { type: 'code-block', hotkey: codeBlockHotkey },
    { type: 'heading-one', hotkey: headingOneHotkey },
    { type: 'heading-two', hotkey: headingTwoHotkey },
    { type: 'heading-three', hotkey: headingThreeHotkey },
    { type: 'numbered-list', hotkey: numberedListHotkey },
    { type: 'bulleted-list', hotkey: bulletedListHotkey },
    { type: 'block-quote', hotkey: blockQuoteHotkey },
] as const;

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('handleKeyDownEvent', () => {
        it.todo('🤷🏼');
    });
}

// #endregion
